import React, { FC, useCallback, useEffect } from 'react';

import { useLocation } from 'react-router-dom';

import { useModalContext } from '~/contexts/modal/ModalContext';
import useDebounceValue from '~/hooks/useDebounceValue';
import BlackFridayModal from '~/modules/black-friday/components/BlackFridayModal';
import useBlackFriday from '~/modules/black-friday/hooks/useBlackFriday';
import analytics from '~/services/analytics';
import { useActions } from '~/store/hooks/useActions';
import { useAppSelector } from '~/store/hooks/useAppSelector';

import { UserExtraFieldKeyBlackFriday } from '../constants';
import isShowModalForPathname from '../utils/isShowModalForPathname';
import isShowModalForProfile from '../utils/isShowModalForProfile';

const DEBOUNCE_TIME = 3000;

type Props = {
  children?: React.ReactNode;
};

const BlackFridayWrapper: FC<Props> = ({ children }) => {
  const profile = useAppSelector((state) => state.profile);
  const location = useLocation();
  const debouncedProfile = useDebounceValue(profile, DEBOUNCE_TIME);
  const debouncedLocation = useDebounceValue(location, DEBOUNCE_TIME);

  const { updateUserExtraFields } = useActions();
  const { handleOpenModal } = useModalContext();
  const { handleBlackFridayClick, prefetchProductById } = useBlackFriday();

  const showBlackFridayModal = useCallback((): void => {
    if (
      !isShowModalForProfile(debouncedProfile) ||
      !isShowModalForPathname(debouncedLocation.pathname)
    ) {
      return;
    }

    handleOpenModal({
      hideCloseButton: true,
      disableBackdropClick: true,
      paddingSize: '0px',
      onClose: () => {
        analytics.trackEvent('black friday - promo modal dismiss');
        updateUserExtraFields({
          notifications: {
            ...debouncedProfile.extra_fields?.notifications,
            [UserExtraFieldKeyBlackFriday]: 1,
          },
        });
      },
      component: ({ onClose }) => (
        <BlackFridayModal
          onShow={() => {
            prefetchProductById();
          }}
          onSubmit={async () => {
            analytics.trackEvent('black friday - promo modal click');
            updateUserExtraFields({
              notifications: {
                ...debouncedProfile.extra_fields?.notifications,
                [UserExtraFieldKeyBlackFriday]: 1,
              },
            });
            await handleBlackFridayClick();
          }}
          onRefuse={() => {
            analytics.trackEvent('black friday - promo modal dismiss');
            updateUserExtraFields({
              notifications: {
                ...debouncedProfile.extra_fields?.notifications,
                [UserExtraFieldKeyBlackFriday]: 1,
              },
            });
            onClose();
          }}
        />
      ),
    });
  }, [debouncedProfile, debouncedLocation.pathname]);

  useEffect(() => showBlackFridayModal(), [showBlackFridayModal]);

  return <>{children}</>;
};

export { BlackFridayWrapper };
